// Centralized helpers for translation status display.
// Backend exposes public statuses; the UI maps them to display buckets:
//   - Processing / Retry Scheduled
//   - Translated
//   - Translation Error
// Plus a separate "Approved" badge once a linguist saves to TM.

export function getStatusBucket(status) {
  switch ((status || '').toLowerCase()) {
    case 'submitted':
    case 'pending':
    case 'processing':
    case 'retry_pending':
    case 'no-fuzzy-match':
      return 'processing';
    case 'success':
    case 'completed':
    case 'fuzzy-match-found':
    case 'gpt4-adapted':
    case 'custom-translated':
    case 'reviewed':
      return 'translated';
    case 'approved':
    case 'published':
      return 'approved';
    case 'error':
    case 'fail':
    case 'failed':
      return 'error';
    default:
      return 'processing';
  }
}

export function getStatusLabel(status) {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'retry_pending') {
    return 'Retry Scheduled';
  }

  const bucket = getStatusBucket(status);
  switch (bucket) {
    case 'processing':
      return 'Processing';
    case 'translated':
      return 'Translated';
    case 'approved':
      return 'Approved';
    case 'error':
      return 'Translation Error';
    default:
      return 'Processing';
  }
}

export function getStatusClass(status) {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'retry_pending') {
    return 'status-retry-pending';
  }

  const bucket = getStatusBucket(status);
  switch (bucket) {
    case 'processing':
      return 'status-processing';
    case 'translated':
      return 'status-completed';
    case 'approved':
      return 'status-approved';
    case 'error':
      return 'status-error';
    default:
      return 'status-processing';
  }
}

export function isTranslated(status) {
  return getStatusBucket(status) === 'translated';
}

export function isError(status) {
  return getStatusBucket(status) === 'error';
}

export function isProcessing(status) {
  return getStatusBucket(status) === 'processing';
}

export function isRetryPending(status) {
  return (status || '').toLowerCase() === 'retry_pending';
}

// Effective status accounts for the linguist review step: once the
// linguist has approved a translation, the row should read "Approved"
// regardless of the underlying backend status (which usually stays
// "success"/"completed" even after review).
export function getEffectiveStatus(request) {
  if (!request) return 'pending';
  const reviewStatus = (request.review_status || '').toLowerCase();
  if (reviewStatus === 'approved') return 'approved';
  return request.status || 'pending';
}

// Convenience: true when the row is in a state where the linguist
// can take the Review action (i.e. AI translation has finished).
// Approved rows still report `true` here because content guidelines
// evolve over time and linguists need to re-edit previously approved
// translations - the modal handles re-approval idempotently.
// True when the AI pipeline has finished for this row (translated,
// approved, or errored). Used to gate bulk actions that should only
// be available once every row has reached a terminal state.
export function isTerminalStatus(status) {
  const bucket = getStatusBucket(status);
  return bucket === 'translated' || bucket === 'approved' || bucket === 'error';
}

export function isAwaitingReview(request) {
  if (!request) return false;
  return getStatusBucket(request.status) === 'translated';
}
